import React, { useEffect, useState } from 'react';
import type { Key } from '@adobe/react-spectrum';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import OncolensToast from '@/components/OncolensToast';
import { AnnouncementsTextArea } from '../components/AnnouncementsTextArea';
import { Button } from '@/components/v2/Button';
import { ConferenceNameInput } from '../components/ConferenceNameInput';
import { Form } from '@/components/v2/Form/Form';
import { HostOrgSelectInput } from '../components/HostOrgSelectInput';
import { ParentOrgComboBox } from '../components/ParentOrgComboBox';
import { useCreateConference } from '../api/conferences';
import { useGetEntireOrgList } from '@/features/organizations';

const formSchema = z.object({
  conferenceName: z.string(),
  hostOrg: z.string(),
  announcements: z.string().optional(),
});

export const AddConference = ({
  setIsAddConference,
}: {
  setIsAddConference: (value: boolean) => void;
}) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      conferenceName: undefined,
      hostOrg: undefined,
      announcements: undefined,
    },
  });
  const [selectedParentOrg, setSelectedParentOrg] = useState<Key>('');
  const createConference = useCreateConference();

  const { data: organizations } = useGetEntireOrgList();

  const parentOrgs = organizations?.map((org) => {
    if (org.parentOrganization === null) {
      return org;
    }
    return org.parentOrganization;
  });

  const newParentOrgs = parentOrgs
    ?.filter((org, index, self) => index === self.findIndex((t) => t?.id === org?.id))
    .map((org) => ({ id: org?.id, name: org?.name }));

  const sortedNewParentOrgs = newParentOrgs?.sort((a, b) => {
    if (a.name && b.name && a.name < b.name) {
      return -1;
    }
    if (a.name && b.name && a.name > b.name) {
      return 1;
    }
    return 0;
  });

  const hostOrgs = organizations
    ?.filter(
      (org) => org.parentOrganizationId === selectedParentOrg || org.id === selectedParentOrg,
    )
    .sort((a, b) => {
      if (a.name && b.name && a.name < b.name) {
        return -1;
      }
      if (a.name && b.name && a.name > b.name) {
        return 1;
      }
      return 0;
    });

  useEffect(() => {
    if (hostOrgs?.length === 1) {
      form.setValue('hostOrg', hostOrgs[0].id, { shouldTouch: true });
    }
  }, [hostOrgs, form.setValue]);

  const handleSubmit = () => {
    const values = form.getValues();
    createConference.mutate(
      {
        name: values.conferenceName,
        organizationId: values.hostOrg,
        announcement: values.announcements || '',
      },
      {
        onSuccess: () => {
          setIsAddConference(false);
          OncolensToast.success('Conference added successfully');
        },
        onError: () => {
          OncolensToast.error('Error creating conference');
        },
      },
    );
  };

  return (
    <>
      <div className="flex items-end justify-between px-8 mt-12">
        <div className="flex flex-col items-start text-zinc-800">
          <Button
            variant="link"
            testId="go-back-button"
            onClickHandler={() => setIsAddConference(false)}>
            Go back
          </Button>
          <h1 className="text-2xl font-semibold">Create a conference</h1>
        </div>
        <div className="flex gap-4">
          <Button
            variant="link"
            testId="cancel-conference-button"
            onClickHandler={() => setIsAddConference(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!form.getValues().conferenceName || !form.getValues().hostOrg}
            testId="save-conference-button"
            onClickHandler={() => handleSubmit()}>
            Save conference
          </Button>
        </div>
      </div>
      <div className="flex justify-center w-full px-8 pb-20">
        <div className="w-full mt-6 bg-white border rounded-lg shadow-sm">
          <div className="border-b-[#E4E4E7] border-b">
            <p className="font-bold text-sm text-zinc-800 px-6 pb-2 pt-2.5">Overview</p>
          </div>
          <div className="px-6 py-8">
            <Form {...form}>
              <form className="w-full gap-6 md:flex">
                <div className="w-full max-w-xs flex flex-col gap-5 mt-[-4px]">
                  <ConferenceNameInput control={form.control as any} name="conferenceName" />
                  <ParentOrgComboBox
                    newParentOrgs={newParentOrgs}
                    selectedParentOrg={selectedParentOrg}
                    setSelectedParentOrg={setSelectedParentOrg}
                    sortedNewParentOrgs={sortedNewParentOrgs}
                  />
                  <HostOrgSelectInput
                    control={form.control}
                    hostOrgs={hostOrgs}
                    selectedParentOrg={selectedParentOrg}
                  />
                </div>
                <div className="mt-6 w-full h-full md:mt-[-3px]">
                  <AnnouncementsTextArea control={form.control as any} name="announcements" />
                </div>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
};
